import { useMemo } from 'react';

import { FieldKey, KybField } from 'apis/legitimizer/types';

import {
  FieldConfig,
  BankDetailsFormFieldsConfig,
  BasicInfoFormFieldsConfig,
  BeneficiaryDetailsFieldsConfig,
  BusinessRepresentativeFieldsConfig,
  DocumentsFormFieldsConfig,
  PEPDetailsFieldsConfig,
  SectionConfiguration,
} from './types';

const getFieldConfig = (fieldKey: FieldKey, keybFields: KybField[]): FieldConfig => {
  const kybFieldResult = keybFields.find(kybField => kybField.key === fieldKey);
  return {
    visible: !!kybFieldResult,
    required: kybFieldResult?.options?.optional === false,
  };
};

const isBusinessRepresentativeRequired = (kybFields: KybField[]): boolean =>
  !!kybFields.find(kybField => kybField.options.optional === false);

export type UseRequiredFieldsMemoType = {
  sectionConfiguration: SectionConfiguration;
  entityUUID?: boolean;
};

export const useBankDetailsFieldsConfigMemo = ({
  sectionConfiguration,
  entityUUID = false,
}: UseRequiredFieldsMemoType): BankDetailsFormFieldsConfig => {
  return useMemo(() => {
    const kybFields = sectionConfiguration['bank-details']?.bankAccountDetails ?? [];
    return {
      entityUUID: { visible: entityUUID, required: false },
      accountNumber: getFieldConfig('BANK_ACCOUNT_NUMBER', kybFields),
      accountOwner: getFieldConfig('BANK_ACCOUNT_OWNER', kybFields),
      accountType: getFieldConfig('BANK_ACCOUNT_TYPE', kybFields),
      bankIdentifier: getFieldConfig('BANK_IDENTIFIER', kybFields),
      payoutRecipients: getFieldConfig('BANK_PAYOUT_RECIPIENTS', kybFields),
      bankDocuments: getFieldConfig('BANK_STATEMENT', kybFields),
      nextSection: { visible: false, required: false },
    };
  }, [sectionConfiguration, entityUUID]);
};

export const useBasicInfoFieldsConfigMemo = ({
  sectionConfiguration,
  entityUUID = false,
}: UseRequiredFieldsMemoType): BasicInfoFormFieldsConfig => {
  return useMemo(() => {
    const kybFields = sectionConfiguration['basic-info']?.businessDetails ?? [];
    const businessRepresentativeKybFields = sectionConfiguration['basic-info']?.businessRepresentative ?? [];

    return {
      entityUUID: { visible: entityUUID, required: false },
      companyName: getFieldConfig('COMPANY_NAME', kybFields),
      streetAddress: getFieldConfig('STREET_ADDRESS', kybFields),
      city: getFieldConfig('CITY', kybFields),
      postalCode: getFieldConfig('POSTAL_CODE', kybFields),
      countryCode: getFieldConfig('COUNTRY', kybFields),
      registrationNumber: getFieldConfig('COMPANY_REGISTRATION_NUMBER', kybFields),
      isVatLiable: getFieldConfig('IS_VAT_LIABLE', kybFields),
      vatId: getFieldConfig('VAT_ID', kybFields),
      lucidId: getFieldConfig('LUCID_ID', kybFields),
      tin: getFieldConfig('TIN', kybFields),
      legalForm: getFieldConfig('LEGAL_FORM', kybFields),
      dateOfRegistration: getFieldConfig('COMPANY_REGISTRATION_DATE', kybFields),
      yearOfRegistration: getFieldConfig('COMPANY_REGISTRATION_DATE', kybFields),
      taxCountryCode: getFieldConfig('TAX_COUNTRY', kybFields),
      fieldOfIndustry: getFieldConfig('FIELD_OF_INDUSTRY', kybFields),
      phone: getFieldConfig('PHONE_NUMBER', kybFields),
      businessRepresentatives: {
        visible: businessRepresentativeKybFields.length > 0,
        required: isBusinessRepresentativeRequired(businessRepresentativeKybFields),
      },
      nextSection: { visible: false, required: false },
    };
  }, [sectionConfiguration, entityUUID]);
};

export const useBusinessRepresentativeFieldsConfigMemo = ({
  sectionConfiguration,
  entityUUID = false,
}: UseRequiredFieldsMemoType): BusinessRepresentativeFieldsConfig => {
  return useMemo(() => {
    const kybFields = sectionConfiguration['basic-info']?.businessRepresentative ?? [];
    return {
      entityUUID: { visible: entityUUID, required: false },
      name: getFieldConfig('FULL_NAME', kybFields),
      dateOfBirth: getFieldConfig('DATE_OF_BIRTH', kybFields),
      yearOfBirth: getFieldConfig('DATE_OF_BIRTH', kybFields),
      placeOfBirth: getFieldConfig('PLACE_OF_BIRTH', kybFields),
      nationality: getFieldConfig('NATIONALITY', kybFields),
      nationalId: getFieldConfig('NATIONAL_ID', kybFields),
      countryOfResidence: getFieldConfig('COUNTRY', kybFields),
      isMainRepresentative: getFieldConfig('IS_MAIN_REPRESENTATIVE', kybFields),
      userId: getFieldConfig('USER_ID', kybFields),
    };
  }, [sectionConfiguration, entityUUID]);
};

export const useDocumentsFieldsConfigMemo = ({
  sectionConfiguration,
}: UseRequiredFieldsMemoType): DocumentsFormFieldsConfig => {
  return useMemo(() => {
    const config = sectionConfiguration.documents?.documentDetails ?? [];

    return {
      registryExtractDocuments: getFieldConfig('REGISTRY_EXTRACT', config),
      nextSection: { visible: false, required: false },
    };
  }, [sectionConfiguration]);
};

export const usePEPDetailsFieldsConfigMemo = ({
  sectionConfiguration,
  entityUUID = false,
}: UseRequiredFieldsMemoType): PEPDetailsFieldsConfig => {
  return useMemo(() => {
    const kybFields = sectionConfiguration.beneficiaries?.pepDetails ?? [];
    return {
      entityUUID: { visible: entityUUID, required: false },
      name: getFieldConfig('FULL_NAME', kybFields),
      officialPosition: getFieldConfig('OFFICIAL_POSITION', kybFields),
      country: getFieldConfig('COUNTRY', kybFields),
      dateOfExpiration: getFieldConfig('DATE_OF_EXPIRATION', kybFields),
      yearOfExpiration: getFieldConfig('DATE_OF_EXPIRATION', kybFields),
    };
  }, [sectionConfiguration, entityUUID]);
};

export const useBeneficiaryDetailsFieldsConfigMemo = ({
  sectionConfiguration,
  entityUUID = false,
}: UseRequiredFieldsMemoType): BeneficiaryDetailsFieldsConfig => {
  return useMemo(() => {
    const kybFields = sectionConfiguration.beneficiaries?.uboDetails ?? [];
    return {
      entityUUID: { visible: entityUUID, required: false },
      name: getFieldConfig('FULL_NAME', kybFields),
      street: getFieldConfig('STREET_ADDRESS', kybFields),
      city: getFieldConfig('CITY', kybFields),
      postalCode: getFieldConfig('POSTAL_CODE', kybFields),
      country: getFieldConfig('COUNTRY', kybFields),
      nationality: getFieldConfig('NATIONALITY', kybFields),
      nationalId: getFieldConfig('NATIONAL_ID', kybFields),
      dateOfBirth: getFieldConfig('DATE_OF_BIRTH', kybFields),
      yearOfBirth: getFieldConfig('DATE_OF_BIRTH', kybFields),
    };
  }, [sectionConfiguration, entityUUID]);
};
